/**
 * Methods for creating tray, dock, and application icons.
 *
 * @experimental
 * @public
 * @packageDocumentation
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { invoke } from "./invoke.js";
/**
 * Creates a new `NativeImage` instance from dataURL.
 * @public
 */
export async function createFromDataURL(...args) {
    return await invoke({
        namespace: "NativeImage",
        method: "createFromDataURL",
        args,
    });
}
/**
 * Creates a new `NativeImage` instance from path.
 * @public
 */
export async function createThumbnailFromPath(...args) {
    return await invoke({
        namespace: "NativeImage",
        method: "createThumbnailFromPath",
        args,
    });
}
/**
 * Creates a new `NativeImage` instance from path.
 * @public
 */
export async function createFromPath(...args) {
    return await invoke({
        namespace: "NativeImage",
        method: "createFromPath",
        args,
    });
}
/**
 * Creates a new `NativeImage` instance from a bitmap buffer.
 * @public
 */
export async function createFromBitmap(...args) {
    return await invoke({
        namespace: "NativeImage",
        method: "createFromBitmap",
        args,
    });
}
/**
 * Creates a new `NativeImage` instance from a buffer.
 * @public
 */
export async function createFromBuffer(...args) {
    return await invoke({
        namespace: "NativeImage",
        method: "createFromBuffer",
        args,
    });
}
/**
 * Creates a new `NativeImage` instance from an NS image that maps to an image name.
 * @public
 */
export async function createFromNamedImage(...args) {
    return await invoke({
        namespace: "NativeImage",
        method: "createFromNamedImage",
        args,
    });
}
/**
 * Creates a new `NativeImage`.
 * @public
 */
export async function createEmpty(...args) {
    return await invoke({
        namespace: "NativeImage",
        method: "createEmpty",
        args,
    });
}
/**
 * returns a Buffer that contains the image's PNG encoded data.
 *
 * @param options - ref: Reference for the NativeImage; scaleFactor: number between 0 and 1.0.
 * @public
 */
export async function toPNG(_a) {
    var { ref } = _a, rest = __rest(_a, ["ref"]);
    return invoke({
        instanceRef: ref,
        namespace: "NativeImage",
        method: "toPNG",
        args: [rest],
    });
}
/**
 * returns a Buffer that contains the image's JPEG encoded data.
 *
 * @param options - ref: Reference for the NativeImage; quality: number between 0 and 100.
 * @public
 */
export async function toJPEG({ ref, quality }) {
    return invoke({
        instanceRef: ref,
        namespace: "NativeImage",
        method: "toJPEG",
        args: [quality],
    });
}
/**
 * returns a Buffer that contains a copy of the image's raw bitmap pixel data.
 *
 * @param options - ref: Reference for the NativeImage; scaleFactor: number between 0 and 1.0.
 * @public
 */
export async function toBitmap(_a) {
    var { ref } = _a, rest = __rest(_a, ["ref"]);
    return invoke({
        instanceRef: ref,
        namespace: "NativeImage",
        method: "toBitmap",
        args: [rest],
    });
}
/**
 * returns the data URL of the image.
 *
 * @param options - ref: Reference for the NativeImage; scaleFactor: number between 0 and 1.0.
 * @public
 */
export async function toDataURL(_a) {
    var { ref } = _a, rest = __rest(_a, ["ref"]);
    return invoke({
        instanceRef: ref,
        namespace: "NativeImage",
        method: "toDataURL",
        args: [rest],
    });
}
/**
 * returns a Buffer that contains the image's raw bitmap pixel data.
 *
 * @param options - ref: Reference for the NativeImage; scaleFactor: number between 0 and 1.0.
 * @public
 */
export async function getBitmap(_a) {
    var { ref } = _a, rest = __rest(_a, ["ref"]);
    return invoke({
        instanceRef: ref,
        namespace: "NativeImage",
        method: "getBitmap",
        args: [rest],
    });
}
/**
 * returns a Buffer that stores C pointer to underlying native handle of the image.
 *
 * @param options - ref: Reference for the NativeImage.
 * @public
 */
export async function getNativeHandle({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "NativeImage",
        method: "getNativeHandle",
        args: [],
    });
}
/**
 * returns whether the image is empty.
 *
 * @param options - ref: Reference for the NativeImage.
 * @public
 */
export async function isEmpty({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "NativeImage",
        method: "isEmpty",
        args: [],
    });
}
/**
 * returns the size of the image.
 *
 * @param options - ref: Reference for the NativeImage; scaleFactor: number between 0 and 1.0.
 * @public
 */
export async function getSize({ ref, scaleFactor, }) {
    return invoke({
        instanceRef: ref,
        namespace: "NativeImage",
        method: "getSize",
        args: [scaleFactor],
    });
}
/**
 * Marks the image as a template image.
 *
 * @param options - ref: Reference for the NativeImage; value: boolean;
 * @public
 */
export async function setTemplateImage({ ref, value, }) {
    return invoke({
        instanceRef: ref,
        namespace: "NativeImage",
        method: "setTemplateImage",
        args: [value],
    });
}
/**
 * Returns whether the image is a template image.
 *
 * @param options - ref: Reference for the NativeImage; value: boolean;
 * @public
 */
export async function isTemplateImage({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "NativeImage",
        method: "isTemplateImage",
        args: [],
    });
}
/**
 * Returns the cropped image.
 *
 * @param options - ref: Reference for the NativeImage; height: number; width: number; x: number; y: number;
 * @public
 */
export async function crop(_a) {
    var { ref } = _a, rest = __rest(_a, ["ref"]);
    return invoke({
        instanceRef: ref,
        namespace: "NativeImage",
        method: "crop",
        args: [rest],
    });
}
/**
 * Returns a resized image.
 *
 * @param options - ref: Reference for the NativeImage; width: number height: number; quality: number.
 * @public
 */
export async function resize(_a) {
    var { ref } = _a, rest = __rest(_a, ["ref"]);
    return invoke({
        instanceRef: ref,
        namespace: "NativeImage",
        method: "resize",
        args: [rest],
    });
}
/**
 * Returns a resized image.
 *
 * @param options - ref: Reference for the NativeImage; scaleFactor: number between 0 and 1.0.
 * @public
 */
export async function getAspectRatio({ ref, scaleFactor, }) {
    return invoke({
        instanceRef: ref,
        namespace: "NativeImage",
        method: "getAspectRatio",
        args: [scaleFactor],
    });
}
/**
 * Returns an array of all scale factors corresponding to representations for a given nativeImage.
 *
 * @param options - ref: Reference for the NativeImage.
 * @public
 */
export async function getScaleFactors({ ref }) {
    return invoke({
        instanceRef: ref,
        namespace: "NativeImage",
        method: "getScaleFactors",
        args: [],
    });
}
/**
 * Add an image representation for a specific scale factor. This can be used to explicitly add different scale factor representations to an image.
 *
 * @param options - ref: Reference for the NativeImage; height: number; width: number; scale: number; buffer: Buffer; dataURL: string;
 * @public
 */
export async function addRepresentation(_a) {
    var { ref } = _a, rest = __rest(_a, ["ref"]);
    return invoke({
        instanceRef: ref,
        namespace: "NativeImage",
        method: "addRepresentation",
        args: [rest],
    });
}
